import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useMemo } from 'react'
import { IExternalCourseParticipant } from 'models/externalCourseParticipant.model'
import UserAvatar from 'components/ui/user-avatar/UserAvatar'
import { joinKeys } from 'helpers/translations.helper'
import { State } from 'components/ui/externalCourses'
import { IAreaModel } from 'models/area.model'
import { IMainState } from 'store/states'
import { useAccess } from 'contexts/AccessContext'
import { prefixTranslation } from '../../../../common'
import Options from '../../Options'

interface UseColumnsProps {
  onDelete: (participant: IExternalCourseParticipant) => void
  onDeleteCertificate: (participant: IExternalCourseParticipant) => void
  onDownloadCertificate: (participant: IExternalCourseParticipant) => void
}

export function useColumns({
  onDelete,
  onDeleteCertificate,
  onDownloadCertificate,
}: UseColumnsProps) {
  const { t } = useTranslation()
  const { access } = useAccess()
  const canViewParticipantProfile = access.canViewParticipantProfile()
  const { data } = useSelector(
    ({ externalCourses }: IMainState) => externalCourses.externalCourse
  )
  type Column = {
    title: string
    key: string | 'action'
    sorteable?: boolean
    dataIndex?: string
    width?: number
    render?: (data: any, all: IExternalCourseParticipant) => any
  }
  const prefix = joinKeys(prefixTranslation, 'participants')
  const prefixColumn = joinKeys(prefix, 'columns')

  const columns: Column[] = useMemo(
    () => [
      {
        title: t(`${prefixColumn}.user.name`),
        key: 'user_full_name',
        dataIndex: 'user_full_name',
        sorteable: true,
        width: 100,
        render: (_value, { user }) => {
          const { id, avatar, fullName, position, abacRole } = user
          const avatarNode = (
            <UserAvatar
              user={{
                fullName: fullName || '',
                position: position || '',
                avatar: avatar || '',
                isDefaultAbacAdmin: !!abacRole?.isDefault,
              }}
              showAdmin
              classes={{ userName: 'text-truncate' }}
            />
          )

          if (!canViewParticipantProfile) {
            return avatarNode
          }

          return (
            <a href={`/configuration/user-profile/${id}`} target="_blank">
              {avatarNode}
            </a>
          )
        },
      },
      {
        title: t(`${prefixColumn}.user.position`),
        key: 'user.position',
        width: 170,
        sorteable: true,
      },
      {
        title: t(`${prefixColumn}.user.area`),
        key: 'user.area',
        width: 170,
        sorteable: true,
        render: (area?: IAreaModel) => area?.name,
      },
      {
        title: t(`${prefixColumn}.state`),
        key: 'state',
        width: 170,
        sorteable: true,
        render: (state: number) => <State state={state} />,
      },
      {
        title: t(`${prefixColumn}.attendance`),
        key: 'attendance',
        width: 80,
        sorteable: true,
        render: (attendance: string) => `${parseInt(attendance)}%`,
      },
      {
        title: t(`${prefixColumn}.totalHours`),
        key: 'totalHours',
        width: 60,
        sorteable: true,
        render: (totalHours: string) =>
          t(`${prefix}.table.columns.totalHours`, { count: +totalHours }),
      },
      {
        title: t(`${prefixColumn}.hasCertificate.title`),
        key: 'certificate',
        width: 110,
        sorteable: true,
        render: (certificate) =>
          certificate
            ? t(`${prefixColumn}.hasCertificate.true`)
            : t(`${prefixColumn}.hasCertificate.false`),
      },
      ...new Array(data?.evaluationsCount || 0).fill(0).map(
        (_, index) =>
          ({
            title: t(`${prefixColumn}.evaluation`, {
              name: index + 1,
            }),
            key: `synchronous_course_evaluations_${index}`,
            width: 120,
            sorteable: false,
            render: (_, { evaluations }: IExternalCourseParticipant) => {
              const evaluation = evaluations?.[index]
              return (
                <div className="text-center">{evaluation?.points || '-'}</div>
              )
            },
          } as Column)
      ),
      {
        title: '',
        key: 'action',
        width: 100,
        render: (
          id: IExternalCourseParticipant['id'],
          participant: IExternalCourseParticipant
        ) => (
          <Options
            participant={participant}
            onDelete={() => onDelete(participant)}
            onDeleteCertificate={() => onDeleteCertificate(participant)}
            onDownloadCertificate={() => onDownloadCertificate(participant)}
          />
        ),
      },
    ],
    [
      canViewParticipantProfile,
      data?.evaluationsCount,
      onDelete,
      onDeleteCertificate,
      onDownloadCertificate,
      prefix,
      prefixColumn,
      t,
    ]
  )
  return columns
}
